/**
 * Single source of truth for the CLI's own version string.
 *
 * Backlog 282 — every site that previously read `package.json` via a
 * relative path got the path wrong once tsup bundled the source into a
 * single `dist/index.js`: `import.meta.url` resolves to the bundle's
 * own location, so `../../package.json` walks two directories above
 * `dist/` and misses `packages/cli/package.json`. Result: 194/194
 * `cli_events` in production had `cli_version: "unknown"`.
 *
 * The walk-up below scans up to six parent directories looking for a
 * `package.json` whose `name` is `@cloudgrid-io/cli`. That makes the
 * lookup work in three layouts:
 *
 *   1. Source / tests — `src/cli-version.ts` → `packages/cli/package.json`.
 *   2. Local build — `dist/index.js` → `packages/cli/package.json`.
 *   3. Installed npm — `<prefix>/lib/node_modules/@cloudgrid-io/cli/dist/index.js`
 *      → the installed package.json one level up from `dist/`.
 *
 * The name check guards against accidentally hitting the monorepo root
 * `package.json` or a shared workspace `package.json` first.
 *
 * `CLOUDGRID_CLI_VERSION` overrides the resolved value when set. Used by
 * the e2e harness so it can assert the CLI it just ran reports a known
 * version.
 */
import { readFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import { dirname, join } from 'node:path';

function resolveCliVersion(): string {
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  let dir = moduleDir;
  for (let i = 0; i < 6; i++) {
    const candidate = join(dir, 'package.json');
    try {
      const pkg = JSON.parse(readFileSync(candidate, 'utf-8'));
      if (pkg.name === '@cloudgrid-io/cli' && typeof pkg.version === 'string') {
        return pkg.version;
      }
    } catch {
      // Try the parent directory.
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return 'unknown';
}

const RESOLVED: string = resolveCliVersion();

/**
 * Resolved CLI version. Returns the env-override when
 * `CLOUDGRID_CLI_VERSION` is set, otherwise the cached value from
 * `package.json`.
 */
export function getCliVersion(): string {
  return process.env.CLOUDGRID_CLI_VERSION || RESOLVED;
}

/**
 * The resolved CLI version, cached at module load. Most callers should
 * use {@link getCliVersion} so the env override is honoured; this export
 * exists for sites that already have version-string handling and only
 * need the static value.
 */
export const CLI_VERSION: string = RESOLVED;
